import React, { useEffect } from "react";
import {
  FaHeadphones,
  FaMusic,
  FaMicrophone,
  FaPlay,
  FaVolumeUp,
} from "react-icons/fa";
import { MdGraphicEq } from "react-icons/md";
import AOS from "aos";
import "aos/dist/aos.css";
import { Link } from "react-router-dom";

const IMAGES = {
  hero: "/assets/images.jpeg",
};

const Audio = () => {
  useEffect(() => {
    AOS.init({ duration: 700, once: true });
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: (
        <FaHeadphones className="text-indigo-500 text-4xl animate-bounce" />
      ),
      title: "High-Fidelity Monitoring",
      desc: "Listen and edit with studio-quality audio precision.",
    },
    {
      icon: <MdGraphicEq className="text-pink-500 text-4xl animate-pulse" />,
      title: "Waveform Editing",
      desc: "Cut, trim, and manipulate audio waveforms easily.",
    },
    {
      icon: (
        <FaMicrophone className="text-green-500 text-4xl animate-spin-slow" />
      ),
      title: "Recording Tools",
      desc: "Record vocals and instruments with professional features.",
    },
    {
      icon: <FaMusic className="text-orange-500 text-4xl animate-bounce" />,
      title: "Music Production",
      desc: "Compose, mix, and master tracks like a pro.",
    },
    {
      icon: <FaPlay className="text-purple-500 text-4xl animate-pulse" />,
      title: "Playback Control",
      desc: "Preview edits instantly with flexible playback options.",
    },
    {
      icon: <FaVolumeUp className="text-red-500 text-4xl animate-jump" />,
      title: "Sound Effects",
      desc: "Add dynamic effects, EQs, and filters to enhance audio.",
    },
  ];

  const workflow = [
    "Import your audio tracks.",
    "Trim, split, and organize clips.",
    "Apply filters, EQ, and effects.",
    "Mix tracks and adjust volumes.",
    "Export for podcasts, music, or films.",
  ];

  const industries = [
    {
      title: "Podcasting",
      text: "Produce professional-quality podcasts with editing precision.",
    },
    {
      title: "Music Production",
      text: "Create beats, songs, and soundtracks.",
    },
    {
      title: "Film & Media",
      text: "Enhance audio for movies, animations, and videos.",
    },
    { title: "Game Audio", text: "Design immersive soundscapes for games." },
    {
      title: "Streaming & Content",
      text: "Edit live streams and content audio seamlessly.",
    },
    {
      title: "Education & Tutorials",
      text: "Create clear instructional audio and lessons.",
    },
  ];

  const outcomes = [
    "Professional audio editing skills",
    "Music production techniques",
    "Podcast creation mastery",
    "Film & media audio enhancement",
    "Sound design for games",
    "Confidence in using DAWs & tools",
  ];

  return (
    <div className="bg-white text-black min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section
        className="relative flex items-center justify-center text-center py-32 p-6"
        style={{
          backgroundImage: `url(${IMAGES.hero})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="bg-white/90 p-10 rounded-3xl shadow-xl">
          <h1 className="text-5xl font-bold mb-4 animate-float">
            Master Audio Editing
          </h1>
          <p className="text-lg mb-6">
            Learn how to produce, edit, and mix audio like a professional.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-3 bg-indigo-600 text-white font-semibold rounded-full shadow-lg hover:bg-indigo-700 transition-transform transform hover:scale-110 hover:-translate-y-1"
          >
            Enroll Now
          </Link>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-12" data-aos="fade-up">
          Core Features
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {features.map((f, i) => {
            const featureColors = [
              "#ff9800",
              "#4caf50",
              "#2196f3",
              "#e91e63",
              "#9c27b0",
              "#00bcd4",
            ];
            const color = featureColors[i % featureColors.length];

            return (
              <div
                key={i}
                className="relative p-6 bg-white border rounded-2xl shadow-lg cursor-pointer transition-all duration-500 hover:scale-105 hover:rotate-1 hover:shadow-2xl shake-hover"
                data-aos="zoom-in"
                data-aos-delay={i * 100}
              >
                {/* Gradient corner top-left */}
                <div
                  className="absolute top-0 left-0 w-12 h-12 rounded-tl-2xl pointer-events-none"
                  style={{
                    background: `linear-gradient(135deg, ${color}, transparent)`,
                    boxShadow: `0 0 8px ${color}`,
                  }}
                ></div>

                <div className="pt-4 pl-4 animate-bounce-slow">
                  <div className="mb-4 flex justify-center">{f.icon}</div>
                  <h3 className="font-semibold text-xl text-center mb-2">
                    {f.title}
                  </h3>
                  <p className="text-center text-gray-700">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-12">
          <Link
            to="/contact"
            className="inline-block px-8 py-3 bg-indigo-600 text-white font-semibold rounded-full shadow-lg hover:bg-indigo-700 transition-transform transform hover:scale-110 hover:-translate-y-1"
          >
            Enroll Now
          </Link>
        </div>
      </section>

      {/* Creative Workflow Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h3 className="text-3xl font-bold text-center mb-10">
            Creative Workflow
          </h3>
          <div className="flex flex-wrap justify-center gap-6">
            {workflow.map((step, i) => {
              const shadowColors = [
                "#ff9800",
                "#4caf50",
                "#2196f3",
                "#e91e63",
                "#9c27b0",
                "#00bcd4",
              ];
              const shadowColor = shadowColors[i % shadowColors.length];

              return (
                <div
                  key={i}
                  className="min-w-[180px] max-w-[220px] p-6 bg-white rounded-xl flex flex-col items-center text-center transform transition-transform hover:scale-105"
                  style={{ boxShadow: `0 8px 20px ${shadowColor}66` }} // colored shadow with transparency
                  data-aos="zoom-in"
                  data-aos-delay={i * 100}
                >
                  <span className="flex items-center justify-center w-12 h-12 bg-indigo-500 text-white font-bold rounded-full mb-4 animate-bounce">
                    {i + 1}
                  </span>
                  <p className="text-gray-800 font-medium">{step}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Can Benefit Section */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h3 className="text-3xl font-bold text-center mb-12" data-aos="fade-up">
          Who Can Benefit
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((ind, i) => {
            const bgColors = [
              "#e0f7fa",
              "#fff3e0",
              "#f3e5f5",
              "#fce4ec",
              "#e8f5e9",
              "#fff9c4",
            ];
            return (
              <div
                key={i}
                className="corner-card p-6 rounded-xl border shadow cursor-pointer transition-all duration-500 hover:scale-105 hover:-translate-y-2 hover:rotate-1 hover:shadow-2xl shake-hover animate-shake"
                data-aos="fade-up"
                data-aos-delay={i * 100}
                style={{ backgroundColor: bgColors[i % 6] }}
              >
                <div className="mb-4 flex justify-center">
                  <MdGraphicEq className="text-4xl animate-pulse" />
                </div>
                <h4 className="font-semibold text-xl mb-2">{ind.title}</h4>
                <p className="text-gray-700 text-sm">{ind.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* What You’ll Gain Section */}
      <section className="py-16 max-w-6xl mx-auto px-6">
        <h3 className="text-3xl font-bold text-center mb-12" data-aos="fade-up">
          What You’ll Gain
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {outcomes.map((item, i) => {
            const outcomeColors = [
              "#ff9800",
              "#4caf50",
              "#2196f3",
              "#e91e63",
              "#9c27b0",
              "#00bcd4",
            ];
            const color = outcomeColors[i % outcomeColors.length];

            const outcomeIcons = [
              <FaHeadphones />,
              <MdGraphicEq />,
              <FaMicrophone />,
              <FaMusic />,
              <FaPlay />,
              <FaVolumeUp />,
            ];

            const outcomeDescs = [
              "Develop professional audio editing skills for any project.",
              "Learn to produce and mix music like a pro.",
              "Gain expertise in recording vocals and instruments.",
              "Master the art of composing, mixing, and mastering tracks.",
              "Control playback and preview edits instantly.",
              "Apply dynamic effects, EQs, and filters to enhance sound.",
            ];

            return (
              <div
                key={i}
                className="corner-card relative p-6 bg-white border rounded-xl shadow-md hover:shadow-lg transition transform hover:scale-105 shake-hover"
                data-aos="fade-up"
                data-aos-delay={i * 100}
                style={{ backgroundColor: "#ffffff" }}
              >
                {/* Gradient top-left corner */}
                <div
                  className="absolute top-0 left-0 w-12 h-12 rounded-tl-2xl pointer-events-none"
                  style={{
                    background: `linear-gradient(135deg, ${color}, transparent)`,
                    boxShadow: `0 0 8px ${color}`,
                  }}
                ></div>

                {/* Decorative corner bottom-right */}
                <div
                  className="absolute bottom-0 right-0 w-12 h-12 pointer-events-none"
                  style={{
                    borderLeft: "3px solid transparent",
                    borderTop: `3px solid ${color}`,
                    borderBottomRightRadius: "12px",
                    background: `linear-gradient(225deg, ${color}, transparent)`,
                    boxShadow: `0 0 8px ${color}`,
                  }}
                ></div>

                <div className="pt-10 pl-10 text-center me-5 animate-float">
                  {/* Colored icon with border */}
                  <div
                    className="flex justify-center items-center w-16 h-16 mx-auto mb-3 rounded-full border-4 animate-wiggle"
                    style={{
                      color: color,
                      borderColor: color,
                    }}
                  >
                    {outcomeIcons[i % outcomeIcons.length]}
                  </div>

                  <h4 className="text-xl font-semibold mb-2">{item}</h4>
                  <p className="text-gray-700 text-sm mb-5">
                    {outcomeDescs[i % outcomeDescs.length]}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6 animate-pulse">
          <h3 className="text-3xl font-bold text-center mb-8">
            Frequently Asked Questions
          </h3>
          <div className="space-y-4">
            <details className="p-4 border rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors duration-300">
              <summary className="cursor-pointer font-medium text-indigo-700">
                Do I need prior audio experience?
              </summary>
              <p className="mt-2 text-gray-800">
                No, this course guides you from beginner to professional level.
              </p>
            </details>
            <details className="p-4 border rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors duration-300">
              <summary className="cursor-pointer font-medium text-indigo-700">
                Which software will I learn?
              </summary>
              <p className="mt-2 text-gray-800">
                We cover popular DAWs and audio editing tools.
              </p>
            </details>
            <details className="p-4 border rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors duration-300">
              <summary className="cursor-pointer font-medium text-indigo-700">
                Will I get a certificate?
              </summary>
              <p className="mt-2 text-gray-800">
                Yes! A certificate is awarded after completing the course.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-indigo-600 text-white py-12 mt-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold mb-4">
            Ready to Master Audio Editing?
          </h3>
          <p className="mb-6">
            Join thousands of learners and take your audio skills to the next
            level.
          </p>
          <Link
            to="/contact"
            className="inline-block px-6 py-2.5 bg-white text-indigo-600 font-bold rounded-full shadow-md hover:bg-gray-200 transition-transform transform hover:scale-105 hover:-translate-y-0.5"
          >
            Enroll Now
          </Link>
        </div>
      </footer>

      {/* Styles */}
      <style>
        {`
          @keyframes shake {
            0% { transform: translate(0, 0) rotate(0deg); }
            20% { transform: translate(-2px, 2px) rotate(-1deg); }
            40% { transform: translate(2px, -2px) rotate(1deg); }
            60% { transform: translate(-2px, 2px) rotate(0deg); }
            80% { transform: translate(2px, -1px) rotate(1deg); }
            100% { transform: translate(0, 0) rotate(0deg); }
          }
          .shake-hover:hover {
            animation: shake 0.5s ease-in-out;
          }
          .corner-card {
            position: relative;
            overflow: hidden;
          }
          .corner-card::before,
          .corner-card::after {
            content: '';
            position: absolute;
            width: 30px;
            height: 30px;
            border: 3px solid #9333ea; /* purple-600 */
          }
          .corner-card::before {
            top: 0;
            left: 0;
            border-right: none;
            border-bottom: none;
          }
          .corner-card::after {
            bottom: 0;
            right: 0;
            border-left: none;
            border-top: none;
          }
        `}
      </style>
    </div>
  );
};

export default Audio;
